/**
 * `page_view` (CONTRACTS §S.2): tells the API which page the visitor of an open conversation is on, so the operator
 * console can show it. It lives in the loader because the storefront is multi-page and the panel chunk is loaded only
 * when the visitor opens the chat.
 *
 * Rules:
 *  - sent only when this browser has a conversation (`conversation_id` in the widget state). The widget stores it only
 *    after a customer message got its answer, or when a conversation the customer already wrote in is reopened
 *    (previous conversations, resume link): a visitor who never wrote sends nothing;
 *  - service data of the chat, not analytics: no `visitor_id`, no experiment, no consent check;
 *  - one event per (conversation, URL) in a row. The last one sent is remembered in `localStorage`, so a reload does
 *    not repeat it and, with several tabs, the tab the visitor comes back to is reported again;
 *  - only while the tab is visible (a product opened in a background tab is reported when the visitor looks at it);
 *  - account, checkout and order pages are never reported, and only the query parameters that identify the page
 *    content are sent (`reportablePage`);
 *  - navigations without a page load (`history.pushState/replaceState`, back/forward, bfcache) are followed, debounced.
 */
import { loadState } from '../shared/storage';
import type { PageViewEventRequest } from '../types/contracts';
import type { AICommerceConfig } from '../types/sdk';

export interface PageViewDeps {
  fetchImpl?: typeof fetch;
  /** Wait after a history change before reading URL and title (themes set the title after `pushState`). */
  debounceMs?: number;
  /** Page title; the SDK passes the title without the widget's own "(1) new message" prefix. */
  getTitle?: () => string;
}

export interface PageViewHandle {
  /** Sends the current page if the rules above allow it. Safe to call at any time. */
  notify(): void;
  stop(): void;
}

const LAST_PREFIX = 'aicommerce:pv:';
export const PAGE_VIEW_URL_MAX = 2048;
export const PAGE_VIEW_TITLE_MAX = 200;

/** First path segment (after an optional language or shop-id prefix) of the areas that are never reported. */
const PRIVATE_AREAS = new Set(['account', 'my-account', 'checkout', 'checkouts', 'orders', 'order-received']);
/** Query parameters that say which content the page shows; every other one (tokens, keys, tracking) is dropped. */
const KEPT_PARAMS = new Set(['vehicle', 'diameter', 'variant', 'q', 'page', 'sort_by', 'type']);
const KEPT_PREFIX = 'filter.';

/**
 * What `page_view` may say about the current page (§S.2):
 *  - `null` (no event) for the customer account, checkout and order pages: `/account/**` (login, `/account/reset/…`,
 *    `/account/activate/…`, addresses, orders), `/checkout/**`, `/checkouts/**`, `/orders/**`, WooCommerce
 *    `/my-account/**` and `/checkout/order-received/**`, also behind a language prefix (`/it/account`, `/en-gb/account`)
 *    or Shopify's numeric shop id (`/12345/orders/<token>`);
 *  - otherwise origin + path + only the query parameters that identify the content: `vehicle`, `diameter`, `variant`,
 *    `q`, `page`, `sort_by`, `type` and `filter.*`, in their original order. No fragment.
 */
export function reportablePage(loc: Pick<Location, 'origin' | 'pathname' | 'search'> = window.location): { url: string; path: string } | null {
  const segments = loc.pathname.toLowerCase().split('/').filter(Boolean);
  if (segments.length > 1 && /^([a-z]{2}(-[a-z0-9]{2,4})?|\d+)$/.test(segments[0]!)) segments.shift();
  if (segments[0] && PRIVATE_AREAS.has(segments[0])) return null;
  const kept = new URLSearchParams();
  try {
    new URLSearchParams(loc.search).forEach((value, key) => {
      const k = key.toLowerCase();
      if (KEPT_PARAMS.has(k) || k.startsWith(KEPT_PREFIX)) kept.append(key, value);
    });
  } catch {
    /* malformed query: reported without it */
  }
  const query = kept.toString();
  return { url: `${loc.origin}${loc.pathname}${query ? `?${query}` : ''}`.slice(0, PAGE_VIEW_URL_MAX), path: loc.pathname.slice(0, PAGE_VIEW_URL_MAX) };
}

export function startPageViews(config: AICommerceConfig, deps: PageViewDeps = {}): PageViewHandle {
  const base = config.apiBase.replace(/\/+$/, '');
  const doFetch: typeof fetch = (...a) => (deps.fetchImpl ?? window.fetch.bind(window))(...a);
  const lastKey = LAST_PREFIX + config.tenantKey;
  // `page_type` and the product come from the server-rendered page: they hold only until the path changes.
  const initialPath = window.location.pathname;
  let stopped = false;
  let inFlight: string | null = null;
  let lastSent: string | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const readLast = (): string | null => {
    try {
      return window.localStorage.getItem(lastKey) ?? lastSent;
    } catch {
      return lastSent;
    }
  };
  const writeLast = (key: string) => {
    lastSent = key;
    try {
      window.localStorage.setItem(lastKey, key);
    } catch {
      /* storage blocked: de-duplicated in memory for this page only */
    }
  };

  const notify = () => {
    if (stopped || document.visibilityState === 'hidden') return;
    const { session_id, conversation_id } = loadState(config.tenantKey);
    if (!session_id || !conversation_id) return;
    const page = reportablePage();
    if (!page) return; // account / checkout / order pages are never reported
    const { url } = page;
    const key = `${conversation_id}|${url}`;
    if (key === inFlight || key === readLast()) return;

    const samePage = window.location.pathname === initialPath;
    const productId = samePage ? (config.productId ?? config.context?.product_id) : undefined;
    const body: PageViewEventRequest = {
      session_id,
      conversation_id,
      type: 'page_view',
      page: {
        page_type: ((samePage ? config.context?.page_type : undefined) ?? 'other').slice(0, 32),
        path: page.path,
      },
      properties: {
        url,
        title: (deps.getTitle?.() ?? document.title ?? '').trim().slice(0, PAGE_VIEW_TITLE_MAX),
        ...(productId ? { product_id: String(productId) } : {}),
      },
    };
    inFlight = key;
    const done = (ok: boolean) => {
      if (inFlight === key) inFlight = null;
      if (ok) writeLast(key); // a failed send is retried by the next trigger
    };
    try {
      doFetch(`${base}/v1/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Key': config.tenantKey, 'X-Session-Id': session_id },
        body: JSON.stringify(body),
        keepalive: true,
        credentials: 'omit',
        mode: 'cors',
      }).then(
        (r) => done(r.ok),
        () => done(false),
      );
    } catch {
      done(false);
    }
  };

  const schedule = () => {
    if (stopped) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      notify();
    }, deps.debounceMs ?? 1000);
  };

  // Navigations without a page load. The originals are called first and untouched; our part can never throw into the theme.
  const hist = window.history;
  const original = { pushState: hist.pushState, replaceState: hist.replaceState };
  const wrap = (name: 'pushState' | 'replaceState') => {
    const native = original[name];
    const wrapped = function (this: History, ...args: Parameters<History['pushState']>) {
      const out = native.apply(this, args);
      try {
        schedule();
      } catch {
        /* ignore */
      }
      return out;
    };
    try {
      hist[name] = wrapped;
    } catch {
      /* frozen history object: back/forward and page loads are still reported */
    }
    return wrapped;
  };
  const wrapped = { pushState: wrap('pushState'), replaceState: wrap('replaceState') };
  const onVisible = () => {
    if (document.visibilityState !== 'hidden') notify();
  };
  window.addEventListener('popstate', schedule);
  window.addEventListener('pageshow', notify);
  document.addEventListener('visibilitychange', onVisible);

  return {
    notify,
    stop() {
      stopped = true;
      if (timer) clearTimeout(timer);
      timer = null;
      window.removeEventListener('popstate', schedule);
      window.removeEventListener('pageshow', notify);
      document.removeEventListener('visibilitychange', onVisible);
      // put the natives back only if nobody wrapped on top of us (otherwise our wrapper stays, inert)
      for (const name of ['pushState', 'replaceState'] as const) {
        try {
          if (hist[name] === wrapped[name]) hist[name] = original[name];
        } catch {
          /* ignore */
        }
      }
    },
  };
}
